import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import { JSONSchema7 } from 'json-schema';

import { Box } from '@mui/material';

import { axiosRepoIntance } from '../../../services/api-client';
import { Branch, Commit } from '../../../services/repo-service';
import { BLOCK_FILE_PATH } from '../../../utils/constants/block';
import { OWNER, Repo_Type } from '../../../utils/constants/gitea';
import { getFormattedDate, getUserNameFromEmail } from '../../../utils/constants/text-field';
import { getSchemaWithSources, JSONSchema } from '../../json-schema-viewer';
import { addPropertiesToRootSchema } from '../../json-schema-viewer/components';
import { sourceDataObject } from '../../json-schema-viewer/components/SchemaRow/state';
import useParentNodeOutputSchema from '../hooks/useParentNodeOutputSchema';
import useReactFlowHelpers from '../hooks/useReactFlowHelpers';
import { BaseNodeProps } from '../nodes/BaseNode';
import { transformerNodeConfigSchema, transformerNodeConfigUiSchema } from '../schema/transformerNodeConfigSchema';
import CustomAutocomplete from './CustomAutocomplete';
import CustomBaseInputTemplate from './CustomBaseInputTemplate';
import CustomJsonSchemaField from './CustomJsonSchemaField';
import CustomSelect from './CustomSelect';
import CustomTitleFieldTemplate from './CustomTitleFieldTemplate';


const widgets: any = {
  customSelect: CustomAutocomplete,
  SelectWidget: CustomSelect,
};

const templates = {
  BaseInputTemplate: CustomBaseInputTemplate,
  TitleFieldTemplate: CustomTitleFieldTemplate,
};

const fields = {
  customJsonTree: CustomJsonSchemaField,
};

interface TransformerNodeConfigProps {
  drawerContent: BaseNodeProps;
  setOpenNodeConfigDrawer: Dispatch<SetStateAction<boolean>>;
  handleFormSubmit: (nodeConfigData: any, root_schema: JSONSchema7, outputVariables?: JSONSchema, extraData?: object) => void;
}

const TransformerNodeConfig: React.FC<TransformerNodeConfigProps> = ({
  drawerContent,
  setOpenNodeConfigDrawer,
  handleFormSubmit,
}) => {

  const parentOutputSchema = useParentNodeOutputSchema(drawerContent.id);
  const { updateRequestNodeData } = useReactFlowHelpers();

  const [schema, setSchema] = useState<JSONSchema7>({});
  const [uiSchema, setUiSchema] = useState<any>({});
  const [formValue, setFormValue] = useState(drawerContent?.nodeConfig || {});
  const [requestSchema, setRequestSchema] = useState<JSONSchema>({});
  const [responseSchema, setResponseSchema] = useState<JSONSchema>({});
  const [repoUrl, setRepoUrl] = useState<string>('');
  const [rootNodeSchema, setRootNodeSchema] = useState<JSONSchema7>({})

  const handleAddSelectTransformerSchema = (list: Branch[]) => {
    if (list.length > 0)
      setRepoUrl(list[0].commit?.url?.split('/commit/')[0].concat('.git') || '');

    setSchema(prevSchema => ({
      ...prevSchema,
      properties: {
        ...prevSchema.properties,
        blockName: {
          type: 'string',
          title: 'Select Transformer',
          enum: list.map(item => item.name).filter(name => name !== undefined) as string[],
        },
      }
    }))
  };

  const handleGetCommitList = (list: Commit[]) => {
    setSchema(prevSchema => ({
      ...prevSchema,
      properties: {
        ...prevSchema.properties,
        commitId: {
          type: 'string',
          title: 'Select Commit',
          enum: list.map(commit => commit.sha).filter((commit): commit is string => commit !== undefined),
          enumNames: list.map(commit => `${commit.sha?.substring(0, 10)} | ${getFormattedDate(new Date(commit.created || ""))} by ${getUserNameFromEmail(commit.author?.email || "")}`),
        },
      }
    }))
  };

  // const handleShowVariableMapping = () => {
  //   setSchema(prevSchema => ({
  //     ...prevSchema,
  //     properties: {
  //       ...prevSchema.properties,
  //       variablesMapping: {
  //         type: 'object',
  //         title: 'Variables Mapping',
  //         description: 'You can map your variables here'
  //       }
  //     }
  //   }))
  // }

  const handleSubmitTransformerConfig = (formData: any) => {
    const json_schema = drawerContent?.nodeConfig ? drawerContent?.nodeConfig?.schema : requestSchema;
    const output_schema = drawerContent?.nodeConfig ? drawerContent?.data?.outputSchema : responseSchema;

    const updatedFormData = {
      ...formData,
      repo_url: repoUrl,
      schema: getSchemaWithSources(json_schema, drawerContent.id)
    }
    console.log("submit transformer config: ", updatedFormData);

    const finalRootSchema: JSONSchema7 = {
      "$schema": "http://json-schema.org/draft-07/schema",
      ...addPropertiesToRootSchema(rootNodeSchema, sourceDataObject)
    };
    console.log('------------->>>final updated root schema ', finalRootSchema)
    const updatedRequestNodeData = updateRequestNodeData({ request_schema: finalRootSchema });

    handleFormSubmit(updatedFormData, updatedRequestNodeData['request_schema'], output_schema,);

    setOpenNodeConfigDrawer(false);
  };

  const getBlocksList = async () => {
    try {
      const response = await axiosRepoIntance.repos.repoListBranches(OWNER, Repo_Type.blocks_Transformer)
      return response.data;
    } catch (error) {
      console.error("Error in getting blocks list:", error);
      alert("Some error occurred.");
    }
  };

  const getAllCommitsList = async (blockName: string) => {
    try {
      const response = await axiosRepoIntance.repos.repoGetAllCommits(OWNER, Repo_Type.blocks_Transformer, { sha: blockName });
      return response.data;
    } catch (error) {
      console.error("Error while getting the commits list:", error);
      alert("Some error occurred while getting commits list.");
    }
  };

  const getBlockFileFromCommit = async (filepath: string, commitId: string) => {

    try {
      const response = await axiosRepoIntance.repos.repoGetContents(OWNER, Repo_Type.blocks_Transformer, filepath, { ref: commitId });
      const content = atob(response.data.content || "");

      // Log the content for debugging purposes
      // console.log(`Fetched content for ${filepath}: `, content);

      return content; // Return the decoded content
    } catch (error: any) {
      alert("An error occurred while fetching the file content");
      console.error("Error fetching file content: ", error);
      throw error;
    }
  };

  useEffect(() => {
    setSchema(transformerNodeConfigSchema);

    return () => { }
  }, [])

  useEffect(() => {
    // if (requestSchema) {
    //   initializeUiSchema(requestSchema, parentOutputSchema);
    // }

    setUiSchema({
      ...transformerNodeConfigUiSchema,
      variablesMapping: {
        "ui:field": "customJsonTree",
        "ui:options": {
          nodeId: drawerContent.id,
          sourceSchema: parentOutputSchema,
          targetSchema: drawerContent?.nodeConfig ? drawerContent?.nodeConfig?.schema : requestSchema,
          updateRootNodeSchemaOnSubmit: (sch: JSONSchema7) => setRootNodeSchema(sch),
        },
      },
    });

    return () => { }
  }, [drawerContent, parentOutputSchema, requestSchema]);

  useEffect(() => {
    const initializeData = async () => {
      const blocksList = await getBlocksList();
      if (!blocksList) return;

      handleAddSelectTransformerSchema(blocksList);

      if (drawerContent?.nodeConfig) {  //Edit mode
        const { blockName } = drawerContent.nodeConfig;

        if (blockName) {
          const commitsList = await getAllCommitsList(blockName);
          if (commitsList) {
            handleGetCommitList(commitsList);
            // handleShowVariableMapping();
          }
        }
      }
      else { // create mode
      }
    }

    initializeData();
  }, [drawerContent]);

  return (
    <Box>
      <Form
        schema={schema}
        uiSchema={uiSchema}
        widgets={widgets}
        fields={fields}
        showErrorList={false}
        autoComplete="off"
        formData={formValue}
        validator={validator}
        templates={templates}
        onChange={({ formData }) => {
          setFormValue(formData);

          if (formData.blockName && formValue.blockName !== formData.blockName) {
            getAllCommitsList(formData.blockName).then(res => {
              if (res) handleGetCommitList(res);
            });
          }

          if (formData.commitId && formValue.commitId !== formData.commitId) {
            Promise.all([
              // getBlockFileFromCommit(BLOCK_FILE_PATH.block_code, formData.commitId),
              getBlockFileFromCommit(BLOCK_FILE_PATH.request_schema, formData.commitId),
              getBlockFileFromCommit(BLOCK_FILE_PATH.response_schema, formData.commitId),
            ]).then(([req_schema, res_schema]) => {

              // if (pCode) handleShowCode(pCode);
              if (req_schema) setRequestSchema(JSON.parse(req_schema));
              if (res_schema) setResponseSchema(JSON.parse(res_schema));

              // handleShowVariableMapping()
            })
          }
        }}
        onSubmit={e => handleSubmitTransformerConfig(e.formData)}
        onError={errors => console.log("Form errors:", errors)}
      />
    </Box>
  );
};

export default TransformerNodeConfig;
